/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  FillOperationParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
} from '@ag-grid-community/core';
import { NewValueParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { AgTooltipComponent } from '@features/inline-budget/ag-tooltip.component';
import {
  WarningModalComponent,
  WarningModalParams,
} from '@features/inline-budget/warning-modal.component';

import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { ActionType, ModifyInvoiceActivityMapping } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { decimalAdd, decimalEquality } from '@shared/utils/floating-math';
import { Utils } from '@shared/utils/utils';

import { MapActivitiesModalTotalRowComponent } from './map-activities-modal-total-row/map-activities-modal-total-row.component';
import { getMapActivitiesGridColumns } from './map-activities-modal.constants';
import {
  MapActivitiesGridRow,
  MapActivitiesModalParams,
  MapActivitiesModalReturnData,
  MapActivitiesTotals,
} from './map-activities-modal.model';
import { MapActivitiesModalService } from './map-activities-modal.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MapActivitiesModalTotalRowComponent,
    ButtonComponent,
    AgGridAngular,
    InputComponent,
    ReactiveFormsModule,
  ],
  selector: 'aux-map-activities-modal',
  styleUrl: 'map-activities-modal.component.scss',
  templateUrl: 'map-activities-modal.component.html',
})
export class MapActivitiesModalComponent {
  ref = inject(CustomOverlayRef<MapActivitiesModalReturnData, MapActivitiesModalParams>);

  private changeDetectorRef = inject(ChangeDetectorRef);
  private globalStore = inject(GlobalStore);
  private launchDarklyService = inject(LaunchDarklyService);
  private mapActivitiesModalService = inject(MapActivitiesModalService);
  private overlayService = inject(OverlayService);

  totals = signal<MapActivitiesTotals>({
    ACTIVITY_DISCOUNT: 0,
    ACTIVITY_INVESTIGATOR: 0,
    ACTIVITY_PASSTHROUGH: 0,
    ACTIVITY_SERVICE: 0,
    invoice_total: 0,
  });

  invoice = this.ref.data?.invoice || ({} as InvoiceModel);

  allocatedAmountsMatch = computed(() => {
    return (
      decimalEquality(
        this.invoice.expense_amounts.discount_total.value || 0,
        this.totals().ACTIVITY_DISCOUNT,
        2
      ) &&
      decimalEquality(
        this.invoice.expense_amounts.investigator_total.value || 0,
        this.totals().ACTIVITY_INVESTIGATOR,
        2
      ) &&
      decimalEquality(
        this.invoice.expense_amounts.pass_thru_total.value || 0,
        this.totals().ACTIVITY_PASSTHROUGH,
        2
      ) &&
      decimalEquality(
        this.invoice.expense_amounts.services_total.value || 0,
        this.totals().ACTIVITY_SERVICE,
        2
      )
    );
  });
  totalsEqual = computed(() => {
    return decimalEquality(
      this.invoice.expense_amounts.invoice_total.value,
      this.totals().invoice_total,
      2
    );
  });

  attributes = this.ref.data?.attributes || [];
  budgetActivityOptions = this.ref.data?.budgetActivityOptions || [];
  columnDefs: (ColDef | ColGroupDef)[] = [];
  readonly defaultRowCount = 5;
  gridAPI!: GridApi;
  gridData: MapActivitiesGridRow[] = [];
  gridOptions = <GridOptions>{
    ...TableConstants.DEFAULT_GRID_OPTIONS.EDIT_GRID_OPTIONS,
    defaultColDef: {
      tooltipComponent: AgTooltipComponent,
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      editable: (params) => !params.node.rowPinned,
      maxWidth: 350,
      resizable: true,
    },
    enableFillHandle: true,
    fillOperation: (params) => {
      let index = params.rowNode.rowIndex;

      if (
        (params.column.getColId() === 'invoice_item_description' ||
          params.column.getColId() === 'activity_id') &&
        index != null
      ) {
        switch (params.direction) {
          case 'down':
            index -= 1;
            break;
          case 'left':
          case 'right':
            return false;
          case 'up':
            index += 1;
            break;
        }

        const firstFocusedRowIndex = params.api.getFocusedCell()?.rowIndex;

        if (index < params.api.getDisplayedRowCount() - 1 && firstFocusedRowIndex !== undefined) {
          const starterNode = params.api.getDisplayedRowAtIndex(firstFocusedRowIndex);

          let data = {};

          if (params.column.getColId() === 'invoice_item_description') {
            data = {
              ...params.rowNode.data,
              invoice_item_description: starterNode
                ? starterNode.data.invoice_item_description
                : params.rowNode.data.invoice_item_description,
            };
          }

          if (params.column.getColId() === 'activity_id') {
            const newValue = this.budgetActivityOptions.find(
              ({ id }) =>
                id ===
                (starterNode ? starterNode.data.activity_id : params.rowNode.data.activity_id)
            );

            data = {
              ...params.rowNode.data,
              activity_id: newValue ? newValue.id : null,
              activity_name: newValue ? newValue.name : null,
              activity_type: newValue ? newValue.activity_type : null,
            };
          }

          params.rowNode.updateData(data);
          this.onCellValueChanged(params);
          this.gridAPI.refreshCells({ rowNodes: [params.rowNode] });
        }
      }

      return false;
    },
    tooltipShowDelay: 0,
  };
  initialNoteMessage = this.ref.data?.noteMessage || '';
  invalidRows: Record<string, MapActivitiesGridRow> = {};
  isMapActivitiesEnabled = this.launchDarklyService.$select((f) => f.map_invoice_to_activities_tab);
  loading = true;
  mappingLineItemsType = this.ref.data?.mappingLineItemsType || null;
  noteControl = new FormControl('');
  removedRows: MapActivitiesGridRow[] = [];
  protected readonly utils = Utils;
  validRows: Record<string, MapActivitiesGridRow> = {};

  addNewRow(): void {
    this.gridAPI.applyTransaction({
      add: [
        {
          activity_id: '',
          activity_name: '',
          activity_type: '',
          amount: 0,
          id: Utils.uuid(),
          invoice_item_description: null,
          isNewRow: true,
        },
      ],
    });
    this.gridAPI.ensureIndexVisible(this.gridAPI.getRenderedNodes().length - 1, 'bottom');
  }

  private calculatePinnedBottomData(): {
    amount: number;
  } {
    const columnsWithAggregation = ['amount'] as const;

    this.gridAPI.selectAll();

    return this.gridAPI.getSelectedRows().reduce(
      (acc, row) => {
        for (const key of columnsWithAggregation) {
          const currentVal = acc[key];
          const additionalVal = row[key];

          if (Utils.isNumber(currentVal) && Utils.isNumber(additionalVal)) {
            acc[key] = decimalAdd(currentVal, additionalVal, 2);
          }
        }

        return acc;
      },
      {
        amount: 0,
      }
    );
  }

  private canDeactivate = async () => {
    if (this.hasChanges()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  };

  constructor() {
    this.ref.canDeactivate = this.canDeactivate;

    effect(() => {
      const isMapActivitiesEnabled = this.isMapActivitiesEnabled();

      if (!isMapActivitiesEnabled) {
        this.ref.close();
      }
    });

    setTimeout(() => {
      this.initData();
    });
    this.mapActivitiesModalService.initInvoiceItemOptions();
  }

  private getGridDataByLineItems() {
    return (
      this.mappingLineItemsType === 'pdf'
        ? this.mapActivitiesModalService.getLineItemPDFOptions()
        : this.mapActivitiesModalService.getLineItemIntegrationOptions()
    ).map((item, index) => {
      const row = {
        activity_id: '',
        activity_name: '',
        activity_type: null,
        amount: item.initialItemValue || 0,
        display_order: index,
        id: uuidv4(),
        invoice_item_description: item.value || '',
        isNewLineItemRow: true,
        isNewRow: false,
      };

      this.invalidRows = {
        ...this.invalidRows,
        [row.id]: row,
      };

      return row;
    });
  }

  private hasChanges(): boolean {
    return (
      this.noteControl.value !== this.initialNoteMessage ||
      this.removedRows.length > 0 ||
      !!Object.keys(this.invalidRows).length ||
      !!Object.keys(this.validRows).length
    );
  }

  initData(): void {
    if (this.ref.data && this.ref.data?.items.length > 0) {
      this.gridData = this.ref.data?.items.map((item) => {
        if (
          item.invoice_item_description &&
          !this.mapActivitiesModalService
            .invoiceItemOptions()
            .some((option) => option.value === item.invoice_item_description)
        ) {
          this.mapActivitiesModalService.addInvoiceItemOptions({
            id: uuidv4(),
            value: item.invoice_item_description,
          });
        }

        return {
          ...item,
          isNewRow: false,
        };
      });

      if (this.mappingLineItemsType) {
        this.removedRows = [...this.gridData];
      }
    } else {
      for (let i = 0; i < this.defaultRowCount; i++) {
        this.gridData.push({
          activity_id: '',
          activity_name: '',
          activity_type: null,
          amount: 0,
          display_order: -1,
          id: Utils.uuid(),
          invoice_item_description: null,
          isNewRow: true,
        });
      }
    }

    if (this.mappingLineItemsType) this.gridData = this.getGridDataByLineItems();

    this.columnDefs = getMapActivitiesGridColumns({
      attributes: this.attributes,
      budgetActivityOptions: this.budgetActivityOptions,
      gridData: this.gridData,
      isInvalidRow: this.isInvalidRow,
      onCellValueChanged: this.onCellValueChanged,
      overlayService: this.overlayService,
      removeRow: this.removeRow,
      updateTotals: this.updateTotals,
      validateCell: this.validateCell,
      vendorCurrency: this.invoice.organization.currency,
    });

    this.noteControl.setValue(this.ref.data?.noteMessage || '');
    this.noteControl.markAsPristine();

    this.loading = false;
    this.changeDetectorRef.detectChanges();
  }

  private isAllActivitiesDeleted(): boolean {
    if (
      this.removedRows.length > 0 &&
      !Object.keys(this.invalidRows).length &&
      !Object.keys(this.validRows).length
    ) {
      const oldRows = this.gridAPI.getRenderedNodes().filter(({ data }) => !data.isNewRow) || [];

      return oldRows.length === 0;
    }

    return false;
  }

  isInvalidRow = (params: CellClassParams): boolean => {
    return !!this.invalidRows[params.data.id];
  };

  isSaveButtonDisabled(): boolean {
    return this.hasChanges()
      ? !!Object.keys(this.invalidRows).length ||
          (!this.isAllActivitiesDeleted() && !this.totalsEqual())
      : true;
  }

  mapItemsToRemove(row: MapActivitiesGridRow) {
    return {
      action_type: ActionType.ACTION_TYPE_REMOVE,
      activity_id: row.activity_id,
      amount: row.amount,
      id: row.id,
      invoice_id: this.invoice.id,
      invoice_item_description: row.invoice_item_description,
      vendor_id: this.invoice.organization.id,
    };
  }

  mapValidRowToSave(key: string, i: number) {
    return {
      action_type: this.mappingLineItemsType
        ? ActionType.ACTION_TYPE_CREATE
        : this.validRows[key].isNewRow
          ? ActionType.ACTION_TYPE_CREATE
          : ActionType.ACTION_TYPE_UPDATE,
      activity_id: this.validRows[key].activity_id,
      amount: this.validRows[key].amount,
      display_order: this.validRows[key].isNewRow ? i : this.validRows[key].display_order,
      id: this.validRows[key].id,
      invoice_id: this.invoice.id,
      invoice_item_description: this.validRows[key].invoice_item_description,
      vendor_id: this.invoice.organization.id,
    };
  }

  onCellValueChanged = (params: FillOperationParams | NewValueParams): void => {
    if (Object.prototype.hasOwnProperty.call(params, 'data')) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.validateCell(!params.data.amount || !params.data.activity_id, params.data, params.node);
    }

    if (Object.prototype.hasOwnProperty.call(params, 'rowNode')) {
      this.validateCell(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        !params.rowNode.data.amount || !params.rowNode.data.activity_id,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        params.rowNode.data,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        params.rowNode
      );
    }
  };

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.setBottomData();
    this.updateTotals();
  }

  updateTotals = () => {
    const calculatedTotals: MapActivitiesTotals = {
      ACTIVITY_DISCOUNT: 0,
      ACTIVITY_INVESTIGATOR: 0,
      ACTIVITY_PASSTHROUGH: 0,
      ACTIVITY_SERVICE: 0,
      invoice_total: this.totals().invoice_total,
    };

    this.gridAPI.selectAll();
    this.gridAPI.getSelectedRows().forEach((row) => {
      if (row.activity_type) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        calculatedTotals[row.activity_type] =
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          decimalAdd(calculatedTotals[row.activity_type], row.amount, 2);
      }
    });
    this.totals.set(calculatedTotals);
  };

  removeRow = (rowNode: IRowNode): void => {
    if (!rowNode.data.isNewRow && !rowNode.data.isNewLineItemRow) {
      this.removedRows.push(rowNode.data);
    }

    delete this.invalidRows[rowNode.data.id];
    delete this.validRows[rowNode.data.id];

    this.gridAPI.applyTransaction({ remove: [rowNode.data] });
    this.setBottomData();
    this.updateTotals();
  };

  private reset(): void {
    this.initialNoteMessage = this.noteControl.value || '';
    this.removedRows = [];
    this.invalidRows = {};
    this.validRows = {};
  }

  async save(): Promise<void> {
    this.gridAPI.selectAll();

    if (this.gridAPI.getSelectedRows().length === 0 && this.removedRows.length > 0) {
      const itemsToRemove: ModifyInvoiceActivityMapping[] = [];

      this.removedRows.forEach((row) => {
        itemsToRemove.push(this.mapItemsToRemove(row));
      });

      const isNotesChanged = !this.noteControl.pristine;

      this.reset();

      this.ref.close({
        isNotesChanged: isNotesChanged,
        itemsToSave: itemsToRemove,
        notesMessage: this.noteControl.value || '',
      });

      return;
    }

    if (!this.globalStore.primaryAttribute.attribute_name() && !this.allocatedAmountsMatch()) {
      const ref = this.overlayService.open<'cancel' | 'confirm', WarningModalParams>({
        baseComponent: WarningModalComponent,
        data: {
          cancelLabel: 'Go Back',
          confirmLabel: 'Yes, Proceed',
          header: 'Invoice Allocation Does Not Match',
          maxWidth: 455,
          message:
            'The Invoice allocation to cost categories do not match the cost category totals from the invoice. Are you sure you want to proceed with this activity mapping?',
        },
      });

      const { data: refData } = await firstValueFrom(ref.afterClosed$);

      if (refData !== 'confirm') {
        return;
      }
    }

    const itemsToSave: ModifyInvoiceActivityMapping[] = [];

    if (this.removedRows.length > 0) {
      this.removedRows.forEach((row) => {
        itemsToSave.push({
          action_type: ActionType.ACTION_TYPE_REMOVE,
          activity_id: row.activity_id,
          amount: row.amount,
          id: row.id,
          invoice_id: this.invoice.id,
          invoice_item_description: row.invoice_item_description,
          vendor_id: this.invoice.organization.id,
        });
      });
    }

    let maxDisplayOrder = 0;

    this.gridData.forEach(
      (gd) =>
        (maxDisplayOrder = gd.display_order > maxDisplayOrder ? gd.display_order : maxDisplayOrder)
    );
    let i = maxDisplayOrder;

    if (Object.keys(this.validRows).length) {
      Object.keys(this.validRows).forEach((key) => {
        itemsToSave.push(this.mapValidRowToSave(key, i));
        i++;
      });
    }

    const isNotesChanged = !this.noteControl.pristine;

    this.reset();

    this.ref.close({
      isNotesChanged: isNotesChanged,
      itemsToSave: itemsToSave,
      notesMessage: this.noteControl.value || '',
    });
  }

  saveButtonTooltip(): string {
    if (this.hasChanges()) {
      if (Object.keys(this.invalidRows).length) {
        return MessagesConstants.RESOLVE_ERRORS_TO_CONTINUE;
      } else if (!this.isAllActivitiesDeleted() && !this.totalsEqual()) {
        return MessagesConstants.MAP_ACTIVITIES.AMOUNT_INVOICE_TOTALS_NOT_MATCH;
      } else return '';
    }

    return 'No changes to save.';
  }

  private setBottomData(): void {
    const totals = this.calculatePinnedBottomData();

    this.totals.set({ ...this.totals(), invoice_total: totals.amount });

    this.gridAPI.setGridOption('pinnedBottomRowData', [
      {
        invoice_item_description: 'Total',
        ...totals,
      },
    ]);
  }

  validateCell = (isInvalidValue: boolean, data: MapActivitiesGridRow, node: IRowNode): void => {
    if (data.isNewRow && !data.activity_type && !data.amount && !data.invoice_item_description) {
      delete this.validRows[data.id];
      delete this.invalidRows[data.id];
    } else {
      if (isInvalidValue) {
        this.invalidRows = {
          ...this.invalidRows,
          [data.id]: data,
        };
        delete this.validRows[data.id];
      } else {
        delete this.invalidRows[data.id];
        this.validRows = {
          ...this.validRows,
          [data.id]: data,
        };
      }
    }

    this.setBottomData();
    this.updateTotals();

    if (node) {
      this.gridAPI.refreshCells({
        force: true,
        rowNodes: [node],
      });
    }
  };
}
